import createStyles from "../../pages/onboarding/CreateApplicationStep.module.css";
import styles from "./SharedWithLandlordScreen.module.css";

export type SharedMember = {
  name: string;
  email: string;
  isLead: boolean;
};

type SharedWithLandlordScreenProps = {
  names: string;
  appTitle: string | null;
  members: SharedMember[];
  userEmail: string;
  onDashboard: () => void;
  _t: (key: string, context?: string, ns?: string, args?: any) => string;
  // Individual-profile shares (no application): subtitle uses the tenant
  // name instead of the application title.
  tenantLabel?: string | null;
  // Hidden for individual shares (no other members to email about).
  showBellLine?: boolean;
};

// Shared-with-landlord success for application shares: confirms which
// landlord can now review the property and lists every application member
// (lead first, you second). The subtitle carries the full scope ("with
// every member ... generate the report") so the dashboard CTA stays the
// only dark element. Individual-profile shares (no application) keep the
// same subtitle shape with the tenant name.
const SharedWithLandlordScreen = ({
  names,
  appTitle,
  members,
  userEmail,
  onDashboard,
  _t,
  tenantLabel = null,
  showBellLine = true,
}: SharedWithLandlordScreenProps) => {
  const me = (userEmail ?? "").toLowerCase();

  return (
    <div className={`${createStyles.overlay} modal-fixed-screen`}>
      <main
        className={`${createStyles.card} ${styles.shell} onboarding-modal-surface`}
      >
        <div className={styles.checkWrap} aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path d="m5 12.5 4.5 4.5L19 7.5" />
          </svg>
        </div>
        <div className={styles.heading}>
          <h1>
            {_t("sharedWithLandlord", "Shared with Landlord")} {names}
          </h1>
          <p className={createStyles.intro}>
            {tenantLabel
              ? _t(
                  "sharedSubtitleTenantThey",
                  "They can now see {{tenantName}}'s application with every member and generate the report.",
                  undefined,
                  { tenantName: tenantLabel }
                )
              : _t(
                  "sharedSubtitleAppThey",
                  "They can now see {{applicationName}} with every member and generate the report.",
                  undefined,
                  { applicationName: appTitle ?? "" }
                )}
          </p>
        </div>

        <section
          className={`${createStyles.people} ${styles.members}`}
          aria-label="shared with"
        >
          {members.map((member, index) => {
            const isYou = me !== "" && member.email.toLowerCase() === me;
            const display = isYou ? _t("you", "You") : member.name;
            return (
              <div
                className={`${createStyles.personRow} ${styles.memberRow}`}
                key={member.email || index}
              >
                <span className={createStyles.avatar}>
                  {(display || "S").charAt(0).toUpperCase()}
                </span>
                <div>
                  <div className={createStyles.personName}>
                    <strong>{display}</strong>
                    <span>
                      {member.isLead
                        ? _t("leadTenant", "Lead tenant")
                        : _t("flatmate", "Flatmate")}
                    </span>
                  </div>
                  <p>{member.email}</p>
                </div>
                <span className={createStyles.sharedChip}>
                  {_t("shared", "Shared")}
                </span>
              </div>
            );
          })}
        </section>

        {showBellLine && (
          <p className={styles.bellLine}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 9.5a6 6 0 0 1 12 0c0 4 1.5 5.5 1.5 5.5h-15C4.5 15 6 13.5 6 9.5" />
              <path d="M10 19a2.2 2.2 0 0 0 4 0" />
            </svg>
            <span>
              {_t(
                "everyMemberGetsEmail",
                "Every member gets an email about this share."
              )}
            </span>
          </p>
        )}

        <button
          type="button"
          className={styles.dashboardButton}
          onClick={onDashboard}
        >
          {_t("goToMyDashboard", "Go to my dashboard")}
        </button>
      </main>
    </div>
  );
};

export default SharedWithLandlordScreen;
